import { FormEvent, useCallback, useMemo, useState } from "react";
import { X } from "lucide-react";
import { CircularProgress } from "../CircularProgress/CircularProgress";
import { FormDropdown } from "../FormDropdown/FormDropdown";
import { ModalPortal } from "../ModalPortal/ModalPortal";
import { useNotification } from "../Notification/NotificationProvider";
import { createItCheck } from "../../api/itCheck";
import { useCurrentUserInfo } from "../../hooks/useCurrentUserInfo";
import { useTariffAccess } from "../../hooks/useTariffAccess";
import { getRemainingByCheckType } from "../../utils/tariffAccess";
import "../CreateRequestModal/CreateRequestModal.scss";

export interface CreateItCheckResult {
  id: string;
  openScheduleSetup?: boolean;
}

interface Props {
  onClose: () => void;
  onCreated: (result: CreateItCheckResult) => void;
}

export const CreateItCheckModal = ({ onClose, onCreated }: Props) => {
  const { showNotification } = useNotification();
  const { userLimits } = useCurrentUserInfo();
  const tariffAccess = useTariffAccess();
  const left = useMemo(
    () => getRemainingByCheckType(userLimits, "it"),
    [userLimits]
  );

  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [period, setPeriod] = useState<"once" | "periodic">("once");
  const [nameErr, setNameErr] = useState<string | null>(null);
  const [urlErr, setUrlErr] = useState<string | null>(null);
  const [genErr, setGenErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const periodOpts = useMemo(
    () =>
      tariffAccess.canScanBySchedule
        ? [
            { value: "once" as const, label: "Разово" },
            { value: "periodic" as const, label: "По расписанию" },
          ]
        : [{ value: "once" as const, label: "Разово" }],
    [tariffAccess.canScanBySchedule]
  );

  const submit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      const n = name.trim();
      const u = url.trim();
      let ok = true;
      if (!n) {
        setNameErr("Укажите название продукта");
        ok = false;
      } else setNameErr(null);
      if (!u) {
        setUrlErr("Укажите URL");
        ok = false;
      } else setUrlErr(null);
      if (left != null && left <= 0) {
        setGenErr("Лимит проверок минцифры исчерпан");
        ok = false;
      } else setGenErr(null);
      if (!ok) return;

      setSaving(true);
      createItCheck({ productName: n, url: u })
        .then((item) => {
          if (!item.id) return;
          showNotification({ message: "Заявка создана", variant: "success" });
          onCreated({
            id: item.id,
            openScheduleSetup:
              tariffAccess.canScanBySchedule && period === "periodic",
          });
        })
        .catch(() => undefined)
        .finally(() => setSaving(false));
    },
    [left, name, onCreated, period, showNotification, tariffAccess.canScanBySchedule, url]
  );

  return (
    <ModalPortal>
      <div className="detail-checks-modal-overlay" onClick={onClose}>
        <div className="detail-checks-modal" onClick={(e) => e.stopPropagation()}>
          <div className="detail-checks-modal-head">
            <h3>Создать заявку на проверку минцифры</h3>
            <button
              type="button"
              className="detail-tasklist-close"
              onClick={onClose}
              aria-label="Закрыть"
            >
              <X size={18} />
            </button>
          </div>

          <form
            id="create-it-check-form"
            className="detail-checks-modal-body create-request-modal-form"
            onSubmit={submit}
          >
            <div className="create-request-modal-form-group">
              <label className="create-request-modal-label">Название продукта</label>
              <input
                type="text"
                className={`create-request-modal-input${nameErr ? " create-request-modal-input--error" : ""}`}
                placeholder="Введите название"
                value={name}
                autoFocus
                onChange={(e) => {
                  setName(e.target.value);
                  setNameErr(null);
                  setGenErr(null);
                }}
              />
              {nameErr ? (
                <span className="create-request-modal-field-error">{nameErr}</span>
              ) : null}
            </div>

            <div className="create-request-modal-form-group">
              <label className="create-request-modal-label">URL сайта</label>
              <input
                type="text"
                className={`create-request-modal-input${urlErr ? " create-request-modal-input--error" : ""}`}
                placeholder="https://example.com"
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  setUrlErr(null);
                  setGenErr(null);
                }}
              />
              {urlErr ? (
                <span className="create-request-modal-field-error">{urlErr}</span>
              ) : null}
            </div>

            <div className="create-request-modal-form-group">
              <label className="create-request-modal-label">Периодичность проверок</label>
              <FormDropdown value={period} options={periodOpts} onChange={setPeriod} />
            </div>

            {genErr ? (
              <span className="create-request-modal-general-error">{genErr}</span>
            ) : null}
          </form>

          <div className="detail-checks-modal-footer">
            <button
              type="submit"
              form="create-it-check-form"
              className="create-request-modal-submit"
              disabled={saving}
            >
              {saving ? <CircularProgress size="sm" /> : "Создать"}
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};
